import React, { useState, useEffect, useRef, useCallback } from "react";
import { HardDrive, RefreshCw, X, CheckCircle, AlertTriangle, Image as ImageIcon, Music, Layers, FileCode } from "lucide-react";
import { WORKSPACE_LIMITS } from "../../../constant/workspaceLimits";

/**
 * Format raw bytes into human-readable B, KB, or MB string
 */
function formatSize(bytes) {
  if (!bytes || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Development & Staging Project Size Inspector
 * Renders in top navigation bar to the left of the "New Project" button.
 * Fully opaque white background with high-contrast text for size inspection.
 * Visible in local development and staging builds (REACT_APP_SHOW_SIZE_INSPECTOR === "true" or staging host),
 * but completely hidden in production builds (REACT_APP_SHOW_SIZE_INSPECTOR === "false" and production domain).
 */
export default function ProjectSizeInspector({ projectSizeBytes = 0, isJr = false, isLight = false }) {
  // Render in development and staging, hidden in live production
  const isVisible =
    process.env.NODE_ENV !== "production" ||
    process.env.REACT_APP_SHOW_SIZE_INSPECTOR === "true" ||
    (typeof window !== "undefined" && (
      window.__FORCE_DEV_SIZE_INSPECTOR__ ||
      (window.location && window.location.hostname && window.location.hostname.includes("staging"))
    ));
  const [isOpen, setIsOpen] = useState(false);
  const [breakdown, setBreakdown] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const popoverRef = useRef(null);

  const maxWorkspaceLimit = WORKSPACE_LIMITS?.WORKSPACE_MEMORY_LIMIT_BYTES || 500 * 1024;
  const maxFirebaseLimit = WORKSPACE_LIMITS?.FIREBASE_CLOUD_SAVE_LIMIT_BYTES || 100 * 1024;

  const currentSize = (breakdown && (breakdown.sb3PackageSizeBytes || breakdown.totalSizeBytes)) || projectSizeBytes || 0;
  const isCloudSafe = currentSize <= maxFirebaseLimit;
  const isWorkspaceSafe = currentSize <= maxWorkspaceLimit;

  // Request latest breakdown data from active workspace iframe
  const requestBreakdown = useCallback(() => {
    setIsRefreshing(true);
    const iframe = document.querySelector(
      "iframe[title='Practido Visual Block Studio'], iframe[title='Practido Junior Studio'], iframe[title='Junior Blocks'], iframe[src*='junior.html'], iframe[src*='index.html']"
    );

    if (iframe) {
      // 1. Instantly populate breakdown from synchronous method if available
      try {
        if (iframe.contentWindow && typeof iframe.contentWindow._getProjectAssetBreakdown === "function") {
          const directData = iframe.contentWindow._getProjectAssetBreakdown();
          if (directData) {
            setBreakdown(directData);
          }
        }
      } catch (_) {}

      // 2. Also directly trigger async exact .sb3 measurement
      try {
        const computeExact =
          iframe.contentWindow?.computeExactSb3SizeBytes ||
          iframe.contentWindow?.computeExactJuniorSb3SizeBytes;
        if (typeof computeExact === "function") {
          computeExact(true).then(() => {
            if (typeof iframe.contentWindow._getProjectAssetBreakdown === "function") {
              const freshData = iframe.contentWindow._getProjectAssetBreakdown();
              if (freshData) setBreakdown(freshData);
            }
            setIsRefreshing(false);
          }).catch(() => setIsRefreshing(false));
        }
      } catch (_) {}

      // 3. Post message to iframe for exact measurement and payload sync
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage({ type: "GET_PROJECT_SIZE_DETAILS" }, "*");
        }
      } catch (_) {}
    }

    setTimeout(() => setIsRefreshing(false), 450);
  }, []);

  // Listen for iframe responses
  useEffect(() => {
    const handleMessage = (e) => {
      if (!e.data) return;
      if (e.data.type === "PROJECT_SIZE_DETAILS_RESPONSE" && e.data.payload) {
        setBreakdown(e.data.payload);
        setIsRefreshing(false);
      } else if (e.data.type === "SCRATCH_PROJECT_CHANGE" && e.data.payload?.projectDetails) {
        setBreakdown(e.data.payload.projectDetails);
      } else if (e.data.type === "SEND_PROJECT_JSON" && e.data.payload?.projectDetails) {
        setBreakdown(e.data.payload.projectDetails);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // Fetch data when dropdown opens
  useEffect(() => {
    if (isOpen) {
      requestBreakdown();
    }
  }, [isOpen, requestBreakdown]);

  // Click outside or Escape to dismiss
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isVisible) return null;

  const percentWorkspace = Math.min(100, Math.round((currentSize / maxWorkspaceLimit) * 100));
  const percentFirebase = Math.min(100, Math.round((currentSize / maxFirebaseLimit) * 100));

  // Determine navbar pill status colors
  let statusBg = isCloudSafe
    ? "bg-emerald-50 border-emerald-400 text-emerald-800"
    : isWorkspaceSafe
    ? "bg-amber-50 border-amber-400 text-amber-800"
    : "bg-red-50 border-red-400 text-red-800";

  let dotColor = isCloudSafe
    ? "bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.8)]"
    : isWorkspaceSafe
    ? "bg-amber-500 shadow-[0_0_6px_rgba(245,158,11,0.8)]"
    : "bg-red-500 shadow-[0_0_6px_rgba(239,68,68,0.8)]";

  return (
    <div className="relative inline-block" ref={popoverRef}>
      {/* Dev-Only Size Indicator Button (Left of New Project) */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        title="Project Storage Inspector (Dev Only - Click for full breakdown)"
        aria-label="Project Storage Inspector"
        className={`flex items-center gap-1.5 transition-all cursor-pointer font-bold select-none ${
          isJr
            ? "px-2.5 py-1.5 rounded-xl border-2 shadow-[0_2px_0_rgba(0,0,0,0.08)] text-xs bg-[#FFF6E5] border-[#F59E0B] text-[#78350F] hover:bg-[#FEF08A] active:translate-y-[1px]"
            : `px-2.5 py-1.5 rounded-lg border text-xs shadow-xs hover:brightness-95 active:scale-[0.98] ${statusBg}`
        }`}
      >
        <HardDrive className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="font-mono font-bold">{formatSize(currentSize)}</span>
        <span className={`w-2 h-2 rounded-full ${dotColor}`} />
        <span className="text-[9px] font-extrabold uppercase px-1 py-0.2 rounded bg-black/10 text-slate-700">
          Dev
        </span>
      </button>

      {/* Fully Opaque White Dropdown Breakdown Dialog */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-start justify-end p-4 pt-14 bg-black/30 backdrop-blur-[2px] sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:p-0 sm:bg-transparent sm:backdrop-blur-none"
        >
          <div
            className="w-full sm:w-[480px] max-h-[85vh] overflow-hidden flex flex-col rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.25)] border border-slate-300 animate-in fade-in slide-in-from-top-2 duration-150"
            style={{ backgroundColor: "#ffffff", opacity: 1, color: "#0f172a" }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-shrink-0"
              style={{ backgroundColor: "#f8fafc" }}
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-lg bg-sky-100 border border-sky-300 flex items-center justify-center">
                  <HardDrive className="w-3.5 h-3.5 text-sky-700" />
                </div>
                <h3 className="text-sm font-black text-slate-900 tracking-tight">
                  Project Storage Inspector
                </h3>
                <span className="text-[10px] font-mono font-extrabold uppercase px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-300">
                  Dev Only
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={requestBreakdown}
                  title="Refresh asset sizes"
                  disabled={isRefreshing}
                  className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-200/80 transition disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? "animate-spin text-sky-600" : ""}`} />
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  title="Close inspector"
                  className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-200/80 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div
              className="p-4 overflow-y-auto space-y-4 text-xs"
              style={{ backgroundColor: "#ffffff" }}
            >
              {/* Total & Limits Overview Card */}
              <div
                className="p-4 rounded-xl border border-slate-200 space-y-3 shadow-xs"
                style={{ backgroundColor: "#f8fafc" }}
              >
                <div className="flex items-baseline justify-between">
                  <div>
                    <span className="text-[11px] uppercase tracking-wider text-slate-500 font-bold block">
                      Total Project Size (.sb3 package)
                    </span>
                    <span className="text-2xl font-black text-slate-900 font-mono">
                      {formatSize(currentSize)}
                    </span>
                    <span className="text-[10px] text-slate-500 block font-medium">
                      Exact file size saved to AWS S3 & checked for Firebase limit
                    </span>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${
                        isCloudSafe
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-300"
                          : isWorkspaceSafe
                          ? "bg-amber-100 text-amber-800 border border-amber-300"
                          : "bg-red-100 text-red-800 border border-red-300"
                      }`}
                    >
                      {isCloudSafe ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-600" />
                          Firebase Safe (&lt;100 KB)
                        </>
                      ) : (
                        <>
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                          Exceeds 100 KB Cloud Save
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Progress Bar against 500 KB limit with 100 KB Cloud Marker */}
                <div className="space-y-1">
                  <div className="relative w-full h-3 rounded-full bg-slate-200 overflow-hidden">
                    <div
                      className={`h-full transition-all duration-300 ${
                        isCloudSafe
                          ? "bg-emerald-500"
                          : isWorkspaceSafe
                          ? "bg-amber-500"
                          : "bg-red-500"
                      }`}
                      style={{ width: `${percentWorkspace}%` }}
                    />
                    {/* 100 KB Firebase Marker (20% of 500 KB) */}
                    <div
                      className="absolute top-0 bottom-0 w-1 bg-sky-600 z-10"
                      style={{ left: "20%" }}
                      title="100 KB Firebase Cloud Save Ceiling"
                    />
                  </div>
                  <div className="flex justify-between text-[10px] font-mono text-slate-500 pt-0.5 font-semibold">
                    <span>0 B</span>
                    <span className="text-sky-700 font-bold">100 KB (Firebase Save Limit)</span>
                    <span>500 KB (Workspace Limit)</span>
                  </div>
                </div>

                {/* Metrics Breakdown */}
                <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-200 text-[11px]">
                  <div className="flex items-center justify-between text-slate-700">
                    <span className="text-slate-500 font-medium">Workspace Limit:</span>
                    <span className="font-mono font-bold text-slate-900">{percentWorkspace}% / 500 KB</span>
                  </div>
                  <div className="flex items-center justify-between text-slate-700">
                    <span className="text-slate-500 font-medium">Firebase Ceiling:</span>
                    <span className="font-mono font-bold text-slate-900">{percentFirebase}% / 100 KB</span>
                  </div>
                </div>
                {breakdown?.rawUncompressedBytes && breakdown.rawUncompressedBytes > currentSize && (
                  <div className="pt-2 border-t border-slate-200 text-[11px] text-slate-600 flex items-center justify-between font-mono">
                    <span>Raw Uncompressed Assets: <strong className="text-slate-900">{formatSize(breakdown.rawUncompressedBytes)}</strong></span>
                    <span className="text-emerald-700 font-bold bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                      ↓ {Math.round((1 - currentSize / breakdown.rawUncompressedBytes) * 100)}% Zip (.sb3: {formatSize(currentSize)})
                    </span>
                  </div>
                )}
              </div>

              {/* Assets Breakdown List */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wider text-slate-600 font-bold flex items-center gap-1.5">
                    <Layers className="w-3.5 h-3.5 text-sky-600" />
                    Asset Storage Breakdown ({breakdown?.assets?.length || 0})
                  </span>
                  {breakdown?.projectJsonSizeBytes > 0 && (
                    <span className="text-[10px] text-slate-600 font-mono font-semibold flex items-center gap-1">
                      <FileCode className="w-3 h-3 text-slate-500" />
                      JSON: {formatSize(breakdown.projectJsonSizeBytes)}
                    </span>
                  )}
                </div>

                {(!breakdown?.assets || breakdown.assets.length === 0) ? (
                  <div
                    className="p-4 rounded-xl border border-slate-200 text-center text-slate-600 space-y-1 shadow-xs"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <p className="font-bold text-slate-800">No custom assets in project yet</p>
                    <p className="text-[11px] text-slate-500">
                      Upload an image sprite, backdrop, or sound to inspect live sizes and compression ratios.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200 rounded-xl border border-slate-200 overflow-hidden shadow-xs">
                    {breakdown.assets.map((item, idx) => {
                      const realSize = item.realSizeBytes || item.projectSizeBytes;
                      const projSize = item.projectSizeBytes;
                      const diff = realSize - projSize;
                      const savedPercent = realSize > 0 && diff > 0 ? Math.round((diff / realSize) * 100) : 0;

                      return (
                        <div
                          key={item.id || idx}
                          className="p-2.5 flex items-center justify-between gap-2 hover:bg-slate-50 transition"
                          style={{ backgroundColor: "#ffffff" }}
                        >
                          {/* Asset Icon & Info */}
                          <div className="flex items-center gap-2.5 min-w-0 flex-1">
                            <div className="w-8 h-8 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center flex-shrink-0">
                              {item.category === "Sound" ? (
                                <Music className="w-4 h-4 text-purple-600" />
                              ) : (
                                <ImageIcon className="w-4 h-4 text-sky-600" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-slate-900 truncate max-w-[170px]">
                                  {item.assetName || item.targetName}
                                </span>
                                <span className="text-[9px] font-mono font-bold px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                  {item.format}
                                </span>
                              </div>
                              <span className="text-[10px] text-slate-500 block truncate font-medium">
                                {item.category} • {item.targetName} {item.dimensions ? `(${item.dimensions})` : ""}
                              </span>
                            </div>
                          </div>

                          {/* Sizes & Optimization Badge */}
                          <div className="text-right flex-shrink-0 space-y-0.5">
                            <div className="font-mono font-bold text-slate-900 text-xs">
                              {formatSize(projSize)}
                            </div>
                            {savedPercent > 0 ? (
                              <div className="text-[10px] font-mono text-emerald-700 font-bold bg-emerald-50 px-1 rounded border border-emerald-200">
                                ↓ {savedPercent}% (Real: {formatSize(realSize)})
                              </div>
                            ) : (
                              <div className="text-[10px] font-mono text-slate-500">
                                Real: {formatSize(realSize)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>


            </div>

            {/* Footer */}
            <div
              className="px-4 py-2.5 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-500 flex-shrink-0"
              style={{ backgroundColor: "#f8fafc" }}
            >
              <span className="font-medium">Dynamic Limits: {formatSize(maxWorkspaceLimit)} / {formatSize(maxFirebaseLimit)}</span>
              <span className="font-mono font-bold text-slate-600 uppercase bg-slate-200 px-1.5 py-0.5 rounded">Auto-hidden in prod</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
